// Read and write a field inside a block by a dotted path, e.g. "groups.0.items.1.price".

import type { Block } from '../../shared/blocks';

export function getPath(obj: unknown, path: string): unknown {
  let cur = obj as Record<string, unknown> | unknown[] | undefined;
  for (const k of path.split('.')) {
    if (cur == null) return undefined;
    cur = (cur as Record<string, unknown>)[k] as typeof cur;
  }
  return cur;
}

/** Returns a copy of the block with one field changed. */
export function setPath<T extends Block>(block: T, path: string, value: unknown): T {
  const copy = structuredClone(block) as unknown as Record<string, unknown>;
  const keys = path.split('.');
  let cur: Record<string, unknown> = copy;
  for (let i = 0; i < keys.length - 1; i++) {
    const next = cur[keys[i]];
    if (next == null || typeof next !== 'object') return block;
    cur = next as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]] = value;
  return copy as unknown as T;
}

export function updateBlock(blocks: Block[], id: string, fn: (b: Block) => Block): Block[] {
  return blocks.map((b) => (b.id === id ? fn(b) : b));
}

export function moveBlock(blocks: Block[], id: string, dir: -1 | 1): Block[] {
  const i = blocks.findIndex((b) => b.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= blocks.length) return blocks;
  const out = blocks.slice();
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** Moves a section so it lands in the gap before `before` (an index in the list as it is now). */
export function moveBlockTo(blocks: Block[], id: string, before: number): Block[] {
  const from = blocks.findIndex((b) => b.id === id);
  if (from < 0) return blocks;
  const to = before > from ? before - 1 : before;
  if (to === from) return blocks;
  const out = blocks.slice();
  const [b] = out.splice(from, 1);
  out.splice(Math.max(0, Math.min(to, out.length)), 0, b);
  return out;
}

export function insertAt(blocks: Block[], b: Block, index: number): Block[] {
  const out = blocks.slice();
  out.splice(Math.max(0, Math.min(index, out.length)), 0, b);
  return out;
}

/**
 * Which gap a pointer at `y` is nearest: 0 = above the first section, n = below the last.
 * `rows` are the sections' top and bottom edges, in order.
 */
export function gapAt(rows: { top: number; bottom: number }[], y: number): number {
  const i = rows.findIndex((r) => y < (r.top + r.bottom) / 2);
  return i < 0 ? rows.length : i;
}
